import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCakeCandles,
  faFaceSmile,
  faMobileScreen,
  faBell,
  faSquarePlus,
  faCircleCheck,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { useGetUserStatusQuery, useSetBirthdayMutation } from "../../services/rtkApi/statusApi";
import { useWebPush } from "../../hooks/useWebPush";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { apiClient } from "../../api/client";
import { url } from "../../api";
import IosInstallSteps from "../pwa/IosInstallSteps";

/* ----------------------------- styled shell ----------------------------- */

const Panel = styled.section`
  width: 100%;
  max-width: 560px;
  margin: 8px 0 14px;
  background: #ffffff;
  border: var(--hm-border);
  box-shadow: var(--hm-shadow);
  border-radius: var(--hm-radius);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #1a1a1a;
  color: #fff;
  font-weight: 800;
  font-size: 1.02rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #262626;
  }

  &:focus-visible {
    outline: 2px solid #ffd400;
    outline-offset: -2px;
  }
`;

const HeaderLabel = styled.span`
  white-space: nowrap;
`;

const MiniIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
`;

const MiniIcon = styled.span<{ $bg: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: ${(p) => p.$bg};
  border: var(--hm-border);
  color: #1a1a1a;
  font-size: 0.74rem;
`;

const CountChip = styled.span`
  margin-left: auto;
  background: #ffd400;
  color: #1a1a1a;
  font-size: 0.8rem;
  font-weight: 800;
  border-radius: 999px;
  padding: 2px 9px;
`;

const Chevron = styled.span`
  display: inline-flex;
  font-size: 0.82rem;
  color: #c4c4c4;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  padding: 12px 14px;

  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
`;

const RowMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBadge = styled.div<{ $bg: string }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(p) => p.$bg};
  border: 1.5px solid rgb(35, 35, 35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  color: #1a1a1a;
`;

const TextWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowTitle = styled.div`
  font-weight: 700;
  font-size: 0.98rem;
  color: #1a1a1a;
`;

const RowSub = styled.div`
  font-size: 0.82rem;
  line-height: 1.3;
  color: #5a5a5a;
  margin-top: 1px;
`;

const ActionBtn = styled.button<{ $bg: string; $hover: string }>`
  flex-shrink: 0;
  border: var(--hm-border);
  box-shadow: var(--hm-shadow-sm);
  background: ${(p) => p.$bg};
  color: #1a1a1a;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 8px 13px;
  border-radius: var(--hm-radius);
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s cubic-bezier(0.25, 0.8, 0.25, 1);

  &:hover:not(:disabled) {
    background: ${(p) => p.$hover};
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const Expand = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed rgba(0, 0, 0, 0.22);
`;

const FieldRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const DateInput = styled.input`
  padding: 8px 10px;
  border: 1.5px solid #bbb;
  border-radius: 3px;
  font: inherit;
  font-size: 0.9rem;
`;

const EmojiInput = styled.input`
  width: 64px;
  padding: 8px 10px;
  border: 1.5px solid #bbb;
  border-radius: 3px;
  font-size: 1.2rem;
  text-align: center;
`;

const MiniHint = styled.div`
  font-size: 0.78rem;
  color: #7a7a7a;
  margin-top: 7px;
`;

const ErrText = styled.div`
  font-size: 0.8rem;
  color: #c0392b;
  margin-top: 7px;
`;

const ActionCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  flex-shrink: 0;
`;

const LaterLink = styled.button`
  border: none;
  background: transparent;
  color: #8a8a8a;
  font-size: 0.74rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0 2px;

  &:hover {
    color: #1a1a1a;
  }
`;

/* ------------------------------- helpers -------------------------------- */

type ItemKey = "birthday" | "emoji" | "install" | "notifications";

interface PanelItem {
  key: ItemKey;
  icon: IconDefinition;
  badgeBg: string;
  btnBg: string;
  btnHover: string;
  title: string;
  subtitle: string;
}

/* --------------------------- snooze (Later) ----------------------------- */

const SNOOZE_KEY = "n450s:pending-snooze";
const SNOOZE_MS = 5 * 24 * 60 * 60 * 1000; // "Later" hides an item for 5 days

function readSnooze(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    const now = Date.now();
    const live: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "number" && v > now) live[k] = v; // drop expired
    }
    return live;
  } catch {
    return {};
  }
}

function writeSnooze(map: Record<string, number>): void {
  try {
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/* ------------------------------ component ------------------------------- */

export interface PendingActionsProps {
  /**
   * "home" (default) respects the 5-day "Later" snooze and shows Later buttons.
   * "profile" always shows every still-pending item and hides Later — the
   * profile is the canonical place to find these any time.
   */
  placement?: "home" | "profile";
}

const PendingActions: React.FC<PendingActionsProps> = ({ placement = "home" }) => {
  const respectSnooze = placement === "home";
  const showLater = placement === "home";
  const auth = useAppSelector((s) => s.auth);
  const isLoggedIn = Boolean(auth.token && auth._id);

  const { data: status, refetch: refetchStatus } = useGetUserStatusQuery(undefined, {
    skip: !isLoggedIn,
  });
  const [setBirthday, { isLoading: savingBirthday }] = useSetBirthdayMutation();

  const { isStandalone, isMobile, installAvailable, installMethod, promptInstall } = usePwaInstall();

  const {
    serverConfigured,
    localSubscribed,
    subscribing,
    error: pushError,
    subscribe,
  } = useWebPush(isLoggedIn);

  const pushSupported =
    typeof navigator !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

  // on home the panel starts collapsed (header shows just the pending icons);
  // on the profile it starts open since that's where you go to act on them
  const [collapsed, setCollapsed] = useState(placement !== "profile");
  // per-item 5-day "Later" snooze (home only)
  const [snoozed, setSnoozed] = useState<Record<string, number>>(() => readSnooze());
  // inline editor / expansion state
  const [openKey, setOpenKey] = useState<ItemKey | null>(null);
  const [birthDate, setBirthDate] = useState("");
  const [emoji, setEmoji] = useState("");
  const [emojiErr, setEmojiErr] = useState("");
  const [savingEmoji, setSavingEmoji] = useState(false);
  const [working, setWorking] = useState(false);

  const toggle = useCallback((key: ItemKey) => {
    setOpenKey((cur) => (cur === key ? null : key));
  }, []);

  const handleSaveBirthday = useCallback(async () => {
    if (!birthDate) return;
    try {
      await setBirthday({ birthDate: new Date(birthDate).toISOString() }).unwrap();
      toast.success("Birthday saved! 🎂");
      setOpenKey(null);
      setBirthDate("");
    } catch {
      toast.error("Could not save your birthday. Try again.");
    }
  }, [birthDate, setBirthday]);

  const handleSaveEmoji = useCallback(async () => {
    if (emoji.length === 0 || emoji.length > 2 || !/\p{Emoji}/u.test(emoji)) {
      setEmojiErr("Pick a single emoji.");
      return;
    }
    setSavingEmoji(true);
    setEmojiErr("");
    try {
      const res = await apiClient.post(`${url}/api/edit/add-emoji`, { emoji });
      if (res.status === 200) {
        toast.success(`Nice — ${emoji} is yours now!`);
        setOpenKey(null);
        setEmoji("");
        refetchStatus();
      } else {
        setEmojiErr("Could not save that emoji.");
      }
    } catch (err) {
      // Surface the backend's actual reason instead of a generic message. The emoji is
      // registry-gated, so failures are usually explainable: "This emoji isn't in the
      // registry…", "User already has an emoji.", "Emoji is already taken.", etc. The
      // 400 body is { error }, the 500 body is { message }.
      const data = (
        err as { response?: { data?: { error?: string; message?: string } } }
      )?.response?.data;
      const reason = data?.error || data?.message;
      setEmojiErr(reason || "Could not save that emoji. Try again.");
    } finally {
      setSavingEmoji(false);
    }
  }, [emoji, refetchStatus]);

  const handleInstall = useCallback(async () => {
    if (installMethod === "ios") {
      toggle("install");
      return;
    }
    setWorking(true);
    await promptInstall();
    setWorking(false);
  }, [installMethod, promptInstall, toggle]);

  const handleEnablePush = useCallback(async () => {
    await subscribe();
  }, [subscribe]);

  const handleLater = useCallback((key: ItemKey) => {
    setSnoozed((cur) => {
      const next = { ...cur, [key]: Date.now() + SNOOZE_MS };
      writeSnooze(next);
      return next;
    });
    setOpenKey((cur) => (cur === key ? null : cur)); // close its editor if open
  }, []);

  // which actions are still outstanding?
  const items = useMemo<PanelItem[]>(() => {
    if (!isLoggedIn) return [];
    const out: PanelItem[] = [];

    if (status && !status.birthday) {
      out.push({
        key: "birthday",
        icon: faCakeCandles,
        badgeBg: "#ffd1e8",
        btnBg: "#ffafd6",
        btnHover: "#ff8cc3",
        title: "Set your birthday",
        subtitle: "Unlock birthday perks + a head start on the Art of the Day.",
      });
    }
    if (status && !status.emoji) {
      out.push({
        key: "emoji",
        icon: faFaceSmile,
        badgeBg: "#ffe89a",
        btnBg: "#ffde34",
        btnHover: "#f5cf00",
        title: "Pick your emoji",
        subtitle: "Your little signature across the site.",
      });
    }
    if (isMobile && !isStandalone && installAvailable) {
      out.push({
        key: "install",
        icon: faMobileScreen,
        badgeBg: "#bff5d0",
        btnBg: "#60fac1",
        btnHover: "#36e7a6",
        title: "Add to home screen",
        subtitle: "Full-screen, faster, no browser bar — it'll feel like a real app.",
      });
    }
    if (pushSupported && serverConfigured && localSubscribed === false) {
      out.push({
        key: "notifications",
        icon: faBell,
        badgeBg: "#cfe6ff",
        btnBg: "#9cd0ff",
        btnHover: "#6db8ff",
        title: "Turn on notifications",
        subtitle: "Never miss the daily drop, comments, or card pulls.",
      });
    }
    // on home, hide items the user tapped "Later" on (until the 5 days lapse)
    return respectSnooze
      ? out.filter((it) => !(snoozed[it.key] && snoozed[it.key] > Date.now()))
      : out;
  }, [
    isLoggedIn,
    status,
    isMobile,
    isStandalone,
    installAvailable,
    pushSupported,
    serverConfigured,
    localSubscribed,
    respectSnooze,
    snoozed,
  ]);

  if (items.length === 0) return null;

  const renderAction = (item: PanelItem) => {
    switch (item.key) {
      case "birthday":
        return (
          <ActionBtn $bg={item.btnBg} $hover={item.btnHover} onClick={() => toggle("birthday")}>
            {openKey === "birthday" ? "Close" : "Set"}
          </ActionBtn>
        );
      case "emoji":
        return (
          <ActionBtn $bg={item.btnBg} $hover={item.btnHover} onClick={() => toggle("emoji")}>
            {openKey === "emoji" ? "Close" : "Add"}
          </ActionBtn>
        );
      case "install":
        return (
          <ActionBtn $bg={item.btnBg} $hover={item.btnHover} onClick={handleInstall} disabled={working}>
            {installMethod === "ios" ? (
              <>
                <FontAwesomeIcon icon={faSquarePlus} /> {openKey === "install" ? "Hide" : "How?"}
              </>
            ) : working ? (
              "Opening…"
            ) : (
              "Install"
            )}
          </ActionBtn>
        );
      case "notifications":
        return (
          <ActionBtn
            $bg={item.btnBg}
            $hover={item.btnHover}
            onClick={handleEnablePush}
            disabled={subscribing}
          >
            {subscribing ? "Enabling…" : "Enable"}
          </ActionBtn>
        );
      default:
        return null;
    }
  };

  const renderExpand = (item: PanelItem) => {
    if (openKey !== item.key) return null;
    if (item.key === "birthday") {
      return (
        <Expand>
          <FieldRow>
            <DateInput
              type="date"
              value={birthDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setBirthDate(e.target.value)}
            />
            <ActionBtn
              $bg="#60fac1"
              $hover="#36e7a6"
              onClick={handleSaveBirthday}
              disabled={!birthDate || savingBirthday}
            >
              {savingBirthday ? "Saving…" : "Save"}
            </ActionBtn>
          </FieldRow>
          <MiniHint>On your birthday you get extra perks and AOTD priority. 🎁</MiniHint>
        </Expand>
      );
    }
    if (item.key === "emoji") {
      return (
        <Expand>
          <FieldRow>
            <EmojiInput
              type="text"
              value={emoji}
              maxLength={2}
              placeholder="🙂"
              onChange={(e) => {
                setEmoji(e.target.value);
                setEmojiErr("");
              }}
            />
            <ActionBtn
              $bg="#60fac1"
              $hover="#36e7a6"
              onClick={handleSaveEmoji}
              disabled={savingEmoji || emoji.length === 0}
            >
              {savingEmoji ? "Saving…" : "Save"}
            </ActionBtn>
          </FieldRow>
          {emojiErr ? <ErrText>{emojiErr}</ErrText> : <MiniHint>One emoji that's yours across NΛSOS.</MiniHint>}
        </Expand>
      );
    }
    if (item.key === "install" && installMethod === "ios") {
      return (
        <Expand>
          <IosInstallSteps />
        </Expand>
      );
    }
    return null;
  };

  return (
    <Panel aria-label="Pending actions">
      <Header
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
      >
        <FontAwesomeIcon icon={faCircleCheck} />
        <HeaderLabel>Pending actions</HeaderLabel>
        <MiniIcons>
          {items.map((item) => (
            <MiniIcon key={item.key} $bg={item.badgeBg} title={item.title}>
              <FontAwesomeIcon icon={item.icon} />
            </MiniIcon>
          ))}
        </MiniIcons>
        <CountChip>{items.length}</CountChip>
        <Chevron>
          <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} />
        </Chevron>
      </Header>
      {!collapsed && (
      <List>
        {items.map((item) => (
          <Row key={item.key}>
            <RowMain>
              <IconBadge $bg={item.badgeBg}>
                <FontAwesomeIcon icon={item.icon} />
              </IconBadge>
              <TextWrap>
                <RowTitle>{item.title}</RowTitle>
                <RowSub>{item.subtitle}</RowSub>
              </TextWrap>
              <ActionCol>
                {renderAction(item)}
                {showLater && (
                  <LaterLink type="button" onClick={() => handleLater(item.key)}>
                    Later
                  </LaterLink>
                )}
              </ActionCol>
            </RowMain>
            {item.key === "notifications" && pushError ? <ErrText>{pushError}</ErrText> : null}
            {renderExpand(item)}
          </Row>
        ))}
      </List>
      )}
    </Panel>
  );
};

export default PendingActions;
